import CountryFlag from "react-country-flag";
import { InfiniteData } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import LeaderboardLoading from "./LeaderboardLoading";
import { JSX } from "react";
import clsx from "clsx";

export interface GameSummaryPlayerData {
  playerId: string;
  nationality: string;
  playername: string;
  result: {
    pos: number;
    score: number;
  };
}

export interface GameSummaryLeaderboardData {
  data: {
    node: {
      otherLeaderboards: {
        records: { items: [GameSummaryPlayerData] };
      };
    };
  };
}

interface BirdieStreakLeaderboardProps {
  data:
    | undefined
    | InfiniteData<AxiosResponse<GameSummaryLeaderboardData, any>>;
  isLoading: boolean;
  ErrorMessage: () => JSX.Element | null;
}

const BirdieStreakLeaderboard = ({
  data,
  isLoading,
  ErrorMessage,
}: BirdieStreakLeaderboardProps) => {
  return (
    <div
      role="table"
      className="bg-[#2c2c2c] rounded-[10px] overflow-hidden min-h-[800px]"
    >
      <div
        role="thead"
        className={`
          flex justify-between gap-[5px] md:gap-2.5 bg-tm-c-gray-700
          px-2.5 py-0 lg:p-0
          [&>div]:py-[15px] [&>div]:px-0 [&>div]:flex-1 [&>div]:text-center [&>div]:text-xs lg:[&>div]:text-[15px]
        `}
      >
        <div role="th">Pos</div>
        <div role="th">
          <span className="hidden lg:block">Country</span>
        </div>
        <div role="th" className="!flex-[4] !text-left md:!flex-[5]">
          Player
        </div>
        <div role="th" className="md:!pr-[15px]">
          Birdie streak
        </div>
      </div>
      <div role="tbody">
        <ErrorMessage />

        {data?.pages?.map(
          (page: { data: GameSummaryLeaderboardData }, pageIndex: number) => {
            const itemsLength =
              page?.data?.data?.node?.otherLeaderboards?.records?.items?.length;

            if (pageIndex === 0 && !itemsLength) {
              return (
                <h2
                  key={pageIndex}
                  className="text-tm-c-dark opacity-60 text-[25px] mt-[160px] mx-10 mb-0 text-center"
                >
                  No results found
                </h2>
              );
            }

            return page.data.data.node.otherLeaderboards.records.items.map(
              (player: GameSummaryPlayerData, playerIndex: number) => {
                const currentPos = player.result.pos;

                const prevPlayerInPage =
                  page.data.data.node.otherLeaderboards.records.items[
                    playerIndex - 1
                  ];
                const prevPosInPage = prevPlayerInPage?.result?.pos;

                // Last player of previous page is needed to determine if current player is in the same position group in pagination
                const prevPageLastPos =
                  pageIndex > 0
                    ? data?.pages?.[
                        pageIndex - 1
                      ]?.data?.data?.node?.otherLeaderboards?.records?.items?.at(
                        -1,
                      )?.result?.pos
                    : null;

                // Is this player part of an existing tie group?
                const isPlayerInSameGroup =
                  // first player of a new page but tied with last of previous page
                  (playerIndex === 0 && prevPageLastPos === currentPos) ||
                  // tied with previous player in the same page
                  prevPosInPage === currentPos;

                return (
                  <div
                    role="tr"
                    className={clsx(
                      "group-hover cursor-pointer transition-colors duration-300 lg:hover:bg-[#171717]",
                      {
                        "border-t border-t-tm-c-gray-700": !isPlayerInSameGroup,
                      },
                    )}
                    key={player.playerId}
                  >
                    <div
                      role="tr"
                      className={`
                        flex justify-between gap-[5px] md:gap-2.5 w-full
                        py-0 px-2.5 lg:p-0
                        [&>div]:flex [&>div]:items-center [&>div]:justify-center [&>div]:flex-1 [&>div]:py-[15px] [&>div]:px-0 md:[&>div]:last-of-type:pr-[15px]
                        [&>div]:relative [&>div]:text-center [&>div]:text-xs md:[&>div]:text-[15px]
                      `}
                    >
                      <div role="td">
                        {!isPlayerInSameGroup ||
                        (playerIndex === 0 && pageIndex === 0)
                          ? player.result.pos
                          : ""}
                      </div>
                      <div role="td">
                        {player.nationality && (
                          <CountryFlag
                            countryCode={player.nationality}
                            className="text-base lg:text-xl"
                            svg
                          />
                        )}
                      </div>
                      <div
                        role="td"
                        className="!flex-[4] !text-left md:!flex-[5] !justify-start"
                      >
                        {player.playername}
                      </div>
                      <div role="td" className="md:pr-[15px]">
                        <span className="inline-block bg-tm-c-brand w-[35px] rounded-[3px] lg:w-[45px]">
                          {player.result.score}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              },
            );
          },
        )}
        {isLoading && <LeaderboardLoading loadingLayout="default" />}
      </div>
    </div>
  );
};

export default BirdieStreakLeaderboard;
